import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import FetchFacilityWorkflows from '@Application/Facilities/Workflows/Queries/FetchFacilityWorkflows';
import fetchFacilityWorkflows from '@Application/Facilities/Workflows/fetchFacilityWorkflows';
import { PermissionEnum } from '@Application/Security/Roles';
import findBlockingPreparation from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/ChooseWorkflow/Helpers/findBlockingPreparation';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import useSubjectWorkflowRuns from '@UI/Hooks/useSubjectWorkflowRuns';
import WorkflowCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/ChooseWorkflow/WorkflowCard';
import useSubjectModifiability from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Hooks/useSubjectModifiability';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { css } from '@emotion/react';
import { FileText, Info } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton } from '@verclam/ui-kit';

const screenStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	width: 100%;
	max-width: 51.25rem;
`;

const headingBlockStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const titleStyle = css`
	${theme.typography.pageTitle}
	margin: 0;
	color: ${theme.colors.ink};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		font-size: 2rem;
	}
`;

const introStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	line-height: 1.6;
	color: ${theme.colors.inkSecondary};
`;

const noticeStyle = css`
	display: flex;
	gap: 0.75rem;
	align-items: center;
	padding: 0.875rem 1.125rem;
	border-radius: ${theme.radii.large};
	background-color: ${theme.states.success.surface};
	color: ${theme.states.success.ink};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.55;
`;

const archivedNoticeStyle = css`
	${noticeStyle}
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.solid};
`;

const noticeIconStyle = css`
	flex: none;
`;

const skeletonStackStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

type Props = {
	readonly subject: SubjectAddress;
};

/**
 * Owns the loading, error, empty and nominal branches of the group-choice screen. Three reads feed it:
 * the dossier (to know whether it is archived), the groups proposed in the facility, and the dossier's
 * active preparations, which "Reprendre" points at.
 */
const ChooseWorkflowContent: React.FC<Props> = ({ subject }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const { documentTypes } = useAccountDocumentTypes();
	const hasWritePermission = useHasPermission(
		[PermissionEnum.INTERVIEW_WRITE, PermissionEnum.DOCUMENT_GENERATION_LAUNCH],
		'any',
	);

	const {
		isKnown,
		canModify,
		isArchived,
		error: subjectError,
		refetch: refetchSubject,
	} = useSubjectModifiability(subject);

	const {
		data: workflows,
		error: workflowsError,
		refetch: refetchWorkflows,
	} = useStoreQuery({
		selector: (state) => state.facilityWorkflowsStore.facilityWorkflows,
		stopOn: (value) =>
			value !== null && value.facilityId === facilityId && value.subjectTypeKey === subject.typeKey,
		query: () => dispatch(fetchFacilityWorkflows(new FetchFacilityWorkflows(facilityId, subject))),
		deps: [facilityId, subject],
	});

	const {
		data: preparations,
		error: preparationsError,
		refetch: refetchPreparations,
	} = useSubjectWorkflowRuns(subject);

	const handleRetry = React.useCallback(() => {
		refetchSubject();
		refetchWorkflows();
		refetchPreparations();
	}, [refetchSubject, refetchWorkflows, refetchPreparations]);

	// Only a read that has actually failed is an error; a still-absent value is a loading state
	// (`guides/state-stores.md`, "Ce que `useStoreQuery` ne garantit pas").
	// A failed run list does not block the cards: they are then offered without an existing preparation.
	if (subjectError !== null || workflowsError !== null) {
		return <ErrorState title="Impossible de charger les groupes de documents." onRetry={handleRetry} />;
	}

	if (!isKnown || workflows === null || (preparations === null && preparationsError === null)) {
		return (
			<div css={skeletonStackStyle}>
				<Skeleton height="6rem" animation="wave" shape="rectangle" />
				<Skeleton height="12rem" animation="wave" shape="rectangle" />
			</div>
		);
	}

	const canWrite = hasWritePermission && canModify;

	return (
		<div css={screenStyle}>
			<div css={headingBlockStyle}>
				<h1 css={titleStyle}>Quels documents préparer ?</h1>
				<p css={introStyle}>
					Choisissez un groupe de documents. Vous pourrez rassembler les éléments sur plusieurs
					visites.
				</p>
			</div>
			<div css={noticeStyle} role="note">
				<Info css={noticeIconStyle} size={18} aria-hidden="true" />
				<span>
					Rien n'est généré à cette étape. C'est vous qui déciderez quand générer, une fois les
					éléments rassemblés.
				</span>
			</div>
			<ConditionalLoader data={isArchived}>
				<div css={archivedNoticeStyle} role="note">
					Ce dossier est archivé : aucune préparation ne peut y être ouverte.
				</div>
			</ConditionalLoader>
			<ConditionalLoader
				data={workflows.list.length > 0}
				fallback={
					<EmptyState
						title="Aucun groupe de documents n'est proposé pour votre structure."
						icon={<FileText size={28} />}
					/>
				}
			>
				{workflows.list.map((workflow) => (
					<WorkflowCard
						key={workflow.key}
						subject={subject}
						workflow={workflow}
						existingPreparation={findBlockingPreparation(
							preparations?.inProgress ?? [],
							workflow,
						)}
						documentTypes={documentTypes}
						canWrite={canWrite}
					/>
				))}
			</ConditionalLoader>
		</div>
	);
};

export default ChooseWorkflowContent;
